// src/Invoices.js

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Search,
  Plus,
  Wallet,
  Receipt,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  FileDown,
  CircleDollarSign,
  Clock3,
  Layers3,
  ChevronRight,
} from "lucide-react";

import "./styles/invoices.css";

import { toast } from "react-toastify";

import NewInvoiceModalModern from "./NewInvoiceModalModern";

const API_BASE =
  "https://receivablesbackend.onrender.com/api";

const Invoices = () => {

  // ==================================================
  // STATE
  // ==================================================

  const [invoices, setInvoices] =
    useState([]);

  const [payments, setPayments] =
    useState([]);

  const [customers, setCustomers] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [filter, setFilter] =
    useState("all");


const [dateFilter, setDateFilter] =
  useState("current");

const [startDate, setStartDate] =
  useState("");

const [endDate, setEndDate] =
  useState("");


  const [selectedInvoices, setSelectedInvoices] =
    useState([]);

  const [showNewModal, setShowNewModal] =
    useState(false);

    const [selectedClient, setSelectedClient] =
  useState(null);

const [clientSearch, setClientSearch] =
  useState("");
  
  // ==================================================
  // FETCH
  // ==================================================

  useEffect(() => {

    const fetchData = async () => {

      try {

        const [
          invoiceRes,
          paymentRes,
          customerRes,
        ] = await Promise.all([

          fetch(`${API_BASE}/invoices`),

          fetch(`${API_BASE}/payments`),

          fetch(`${API_BASE}/customers`),

        ]);

        const [
          invoiceData,
          paymentData,
          customerData,
        ] = await Promise.all([

          invoiceRes.json(),

          paymentRes.json(),

          customerRes.json(),

        ]);

        setInvoices(invoiceData);

        setPayments(paymentData);

        setCustomers(customerData);

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);

      }
    };

    fetchData();

  }, []);

  // ==================================================
  // HELPERS
  // ==================================================

  const getCustomer = (
    customerId
  ) => {

    return customers.find(
      (c) => c.id === customerId
    );
  };

  const getInvoicePayments = (
    invoiceNumber
  ) => {

    return payments.filter(
      (p) =>
        p.invoice_number ===
        invoiceNumber
    );
  };

  const getPaidAmount = (
    invoice
  ) => {

    return getInvoicePayments(
      invoice.invoice_number
    ).reduce(
      (sum, p) =>
        sum + Number(p.amount || 0),
      0
    );
  };

  const getRemaining = (
    invoice
  ) => {

    return (
      Number(invoice.amount || 0) -
      getPaidAmount(invoice)
    );
  };

  const getStatus = (
    invoice
  ) => {

    const remaining =
      getRemaining(invoice);

    const dueDate =
      new Date(invoice.due_date);

    const today = new Date();

    if (remaining <= 0) {
      return "paid";
    }

    if (dueDate < today) {
      return "overdue";
    }

    return "outstanding";
  };

  const getHealth = (
    invoice
  ) => {

    const dueDate =
      new Date(invoice.due_date);

    const today = new Date();

    const diff =
      (
        today - dueDate
      ) /
      (
        1000 *
        60 *
        60 *
        24
      );

    if (getStatus(invoice) === "paid") {
      return {
        label: "GOOD",
        className:
          "health-good",
      };
    }

    if (diff > 60) {
      return {
        label: "CRITICAL",
        className:
          "health-critical",
      };
    }

    if (diff > 30) {
      return {
        label: "OVERDUE",
        className:
          "health-overdue",
      };
    }

    return {
      label: "WATCH",
      className:
        "health-watch",
    };
  };



const matchesDateFilter = (
  invoice
) => {

  const invoiceDate =
    new Date(
      invoice.date_created
    );

  const now = new Date();

  // CURRENT MONTH

  if (dateFilter === "current") {

    return (
      invoiceDate.getMonth() ===
        now.getMonth() &&

      invoiceDate.getFullYear() ===
        now.getFullYear()
    );
  }

  // PREVIOUS MONTH

  if (dateFilter === "previous") {

    const previousMonth =
      now.getMonth() - 1;

    return (
      invoiceDate.getMonth() ===
        previousMonth &&

      invoiceDate.getFullYear() ===
        now.getFullYear()
    );
  }

  // CUSTOM RANGE

  if (
    dateFilter === "custom" &&
    startDate &&
    endDate
  ) {

    const start =
      new Date(startDate);

    const end =
      new Date(endDate);

    return (
      invoiceDate >= start &&
      invoiceDate <= end
    );
  }

  // ALL

  return true;
};


  // ==================================================
  // FILTERING
  // ==================================================

  const filteredInvoices =
    useMemo(() => {

      return invoices.filter((inv) => {

        const customer =
          getCustomer(
            inv.client_id
          );

        const customerName =
          (
            customer?.full_name ||
            customer?.name ||
            ""
          ).toLowerCase();

        const matchesSearch =
          inv.invoice_number
            ?.toLowerCase()
            .includes(
              searchTerm.toLowerCase()
            ) ||

          inv.item
            ?.toLowerCase()
            .includes(
              searchTerm.toLowerCase()
            ) ||

          customerName.includes(
            searchTerm.toLowerCase()
          );

        if (!matchesSearch) {
          return false;
        }

        const status =
          getStatus(inv);

// DATE FILTER FIRST

if (!matchesDateFilter(inv)) {
  return false;
}

// STATUS FILTER

if (filter === "all") {
  return true;
}

return status === filter;
      });

}, [
  invoices,
  payments,
  searchTerm,
  filter,
  dateFilter,
  startDate,
  endDate,
]);

  // ==================================================
  // KPI TOTALS
  // ==================================================

  const outstandingTotal =
    invoices.reduce(
      (sum, inv) =>
        sum +
        (
          getRemaining(inv) > 0
            ? getRemaining(inv)
            : 0
        ),
      0
    );

  const overdueTotal =
    invoices
      .filter(
        (i) =>
          getStatus(i) ===
          "overdue"
      )
      .reduce(
        (sum, inv) =>
          sum +
          getRemaining(inv),
        0
      );

  const paidTotal =
    payments.reduce(
      (sum, p) =>
        sum +
        Number(p.amount || 0),
      0
    );

  const thisMonthTotal =
    invoices
      .filter((inv) => {

        const d =
          new Date(
            inv.date_created
          );

        const now =
          new Date();

        return (
          d.getMonth() ===
            now.getMonth() &&
          d.getFullYear() ===
            now.getFullYear()
        );

      })

      .reduce(
        (sum, inv) =>
          sum +
          Number(inv.amount || 0),
        0
      );

  // ==================================================
  // LOADING
  // ==================================================

  if (loading) {

    return (
      <div className="invoice-loading">

        <div className="invoice-loader"></div>

      </div>
    );
  }

  // ==================================================
  // UI
  // ==================================================

  return (

    <div className="invoice-dashboard">

      {/* ========================================== */}
      {/* HERO */}
      {/* ========================================== */}

      <div className="invoice-hero">

        <div>

          <h1>
            Receivables Workspace
          </h1>

          <p>
            Intelligent invoice
            management and
            collection tracking.
          </p>

        </div>

<div className="invoice-hero-actions">

  {/* CLIENT LOOKUP */}

  <div className="invoice-client-lookup">

    <Search size={18} />

    <input
      type="text"
      placeholder="Select customer..."
      value={clientSearch}
      onChange={(e) => {

        setClientSearch(
          e.target.value
        );

        setSelectedClient(null);
      }}
    />

    {clientSearch && !selectedClient && (

      <div className="invoice-client-results">

        {customers
          .filter((c) =>
            (
              c.name ||
              c.full_name ||
              ""
            )
              .toLowerCase()
              .includes(
                clientSearch.toLowerCase()
              )
          )
          .slice(0, 6)
          .map((c) => (

            <div
              key={c.id}
              className="invoice-client-item"
              onClick={() => {

                setSelectedClient(c);

                setClientSearch(
                  c.name ||
                  c.full_name
                );
              }}
            >

              {c.name || c.full_name}

            </div>

          ))}

      </div>

    )}

  </div>

  <button
    className="hero-primary-btn"
    onClick={() => {

      if (!selectedClient) {

        toast.error(
          "Select a customer first."
        );

        return;
      }

      setShowNewModal(true);
    }}
  >

    <Plus size={18} />

    New Invoice

  </button>

  <button
    className="hero-secondary-btn"
  >

    <FileDown size={18} />

    Export

  </button>

</div>
      </div>

      {/* ========================================== */}
      {/* KPI GRID */}
      {/* ========================================== */}

      <div className="invoice-kpi-grid">

        <div className="invoice-kpi-card">

          <div className="kpi-top">

            <div className="kpi-icon teal">

              <Wallet size={22} />

            </div>

          </div>

          <span>
            Outstanding
          </span>

          <strong className="danger-text">

            $
            {outstandingTotal.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

        </div>

        <div className="invoice-kpi-card">

          <div className="kpi-top">

            <div className="kpi-icon red">

              <AlertTriangle
                size={22}
              />

            </div>

          </div>

          <span>
            Overdue
          </span>

          <strong className="danger-text">

            $
            {overdueTotal.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

        </div>

        <div className="invoice-kpi-card">

          <div className="kpi-top">

            <div className="kpi-icon green">

              <CheckCircle2
                size={22}
              />

            </div>

          </div>

          <span>
            Collected
          </span>

          <strong className="success-text">

            $
            {paidTotal.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

        </div>

        <div className="invoice-kpi-card">

          <div className="kpi-top">

            <div className="kpi-icon blue">

              <Calendar size={22} />

            </div>

          </div>

          <span>
            This Month
          </span>

          <strong>

            $
            {thisMonthTotal.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

        </div>

      </div>

      {/* ========================================== */}
      {/* WORKSPACE */}
      {/* ========================================== */}

      <div className="invoice-workspace">

        {/* ====================================== */}
        {/* MAIN */}
        {/* ====================================== */}

        <div className="invoice-main">

          {/* ================================== */}
          {/* FILTERS */}
          {/* ================================== */}

          <div className="invoice-toolbar">

            <div className="invoice-search">

              <Search size={18} />

              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="invoice-filters">

<div className="invoice-date-filters">

  <button
    className={
      dateFilter === "current"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setDateFilter("current")
    }
  >
    Current Month
  </button>

  <button
    className={
      dateFilter === "previous"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setDateFilter("previous")
    }
  >
    Previous Month
  </button>

  <button
    className={
      dateFilter === "all"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setDateFilter("all")
    }
  >
    All Time
  </button>

  <button
    className={
      dateFilter === "custom"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setDateFilter("custom")
    }
  >
    Custom
  </button>

</div>


              <button
                className={
                  filter === "all"
                    ? "filter-active"
                    : ""
                }
                onClick={() =>
                  setFilter("all")
                }
              >
                All
              </button>

              <button
                className={
                  filter ===
                  "outstanding"
                    ? "filter-active"
                    : ""
                }
                onClick={() =>
                  setFilter(
                    "outstanding"
                  )
                }
              >
                Outstanding
              </button>

              <button
                className={
                  filter ===
                  "overdue"
                    ? "filter-active"
                    : ""
                }
                onClick={() =>
                  setFilter("overdue")
                }
              >
                Overdue
              </button>

              <button
                className={
                  filter === "paid"
                    ? "filter-active"
                    : ""
                }
                onClick={() =>
                  setFilter("paid")
                }
              >
                Paid
              </button>

            </div>

          </div>

          {/* ================================== */}
          {/* SELECTION BAR */}
          {/* ================================== */}

          {selectedInvoices.length >
            0 && (

            <div className="selection-bar">

              <div>

                <Layers3 size={18} />

                {
                  selectedInvoices.length
                } selected

              </div>

              <div className="selection-actions">

                <button>

                  <FileDown
                    size={16}
                  />

                  Export

                </button>

                <button>

                  <CircleDollarSign
                    size={16}
                  />

                  Record Payment

                </button>

              </div>

            </div>

          )}

          {/* ================================== */}
          {/* INVOICE GRID */}
          {/* ================================== */}

          <div className="invoice-card-grid">

            {filteredInvoices.map(
              (invoice) => {

                const customer =
                  getCustomer(
                    invoice.client_id
                  );

                const paid =
                  getPaidAmount(
                    invoice
                  );

                const remaining =
                  getRemaining(
                    invoice
                  );

                const progress =
                  (
                    paid /
                    Number(
                      invoice.amount
                    )
                  ) *
                  100;

                const health =
                  getHealth(invoice);

                return (

                  <div
                    key={invoice.id}
                    className={`invoice-card ${
                      selectedInvoices.includes(
                        invoice.id
                      )
                        ? "invoice-selected"
                        : ""
                    }`}
                  >

                    {/* ================= */}
                    {/* TOP */}
                    {/* ================= */}

                    <div className="invoice-card-top">

                      <div>

                        <span>
                          Invoice
                        </span>

                        <h3>
                          {
                            invoice.invoice_number
                          }
                        </h3>

                      </div>

                      <div
                        className={`invoice-health ${health.className}`}
                      >

                        {
                          health.label
                        }

                      </div>

                    </div>

                    {/* ================= */}
                    {/* CUSTOMER */}
                    {/* ================= */}

                    <div className="invoice-client">

                      <strong>

                        {
                          customer
                            ?.full_name ||
                          customer
                            ?.name ||
                          "Unknown"
                        }

                      </strong>

                      <span>

                        {
                          invoice.item
                        }

                      </span>

                    </div>

                    {/* ================= */}
                    {/* VALUES */}
                    {/* ================= */}

                    <div className="invoice-values">

                      <div>

                        <span>
                          Total
                        </span>

                        <strong>

                          $
                          {Number(
                            invoice.amount
                          ).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                            }
                          )}

                        </strong>

                      </div>

                      <div>

                        <span>
                          Paid
                        </span>

                        <strong className="success-text">

                          $
                          {paid.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                            }
                          )}

                        </strong>

                      </div>

                    </div>

                    {/* ================= */}
                    {/* BALANCE */}
                    {/* ================= */}

                    <div className="invoice-balance">

                      <div className="balance-top">

                        <span>
                          Remaining
                        </span>

                        <strong className="danger-text">

                          $
                          {remaining.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                            }
                          )}

                        </strong>

                      </div>

                      <div className="progress-track">

                        <div
                          className="progress-fill"
                          style={{
                            width:
                              `${progress}%`,
                          }}
                        ></div>

                      </div>

                    </div>

                    {/* ================= */}
                    {/* FOOTER */}
                    {/* ================= */}

                    <div className="invoice-card-footer">

                      <div className="invoice-dates">

                        <Clock3
                          size={15}
                        />

                        Due:
                        {" "}
                        {
                          new Date(
                            invoice.due_date
                          ).toLocaleDateString()
                        }

                      </div>

                      <button
                        className="invoice-view-btn"
                      >

                        Open

                        <ChevronRight
                          size={16}
                        />

                      </button>

                    </div>

                    {/* ================= */}
                    {/* SELECT */}
                    {/* ================= */}

                    <div className="invoice-select">

                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(
                          invoice.id
                        )}

                        onChange={() => {

                          setSelectedInvoices(
                            (prev) =>

                              prev.includes(
                                invoice.id
                              )

                                ? prev.filter(
                                    (
                                      id
                                    ) =>
                                      id !==
                                      invoice.id
                                  )

                                : [
                                    ...prev,
                                    invoice.id,
                                  ]
                          );
                        }}
                      />

                    </div>

                  </div>

                );
              }
            )}

          </div>

        </div>

        {/* ====================================== */}
        {/* SIDEBAR */}
        {/* ====================================== */}

        <div className="invoice-sidebar">

          <div className="sidebar-card">

            <h3>
              Collection Insights
            </h3>

            <div className="insight-item">

              <span>
                Outstanding
              </span>

              <strong className="danger-text">

                $
                {outstandingTotal.toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                  }
                )}

              </strong>

            </div>

            <div className="insight-item">

              <span>
                Overdue
              </span>

              <strong className="danger-text">

                $
                {overdueTotal.toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                  }
                )}

              </strong>

            </div>

            <div className="insight-item">

              <span>
                Collection Rate
              </span>

              <strong className="success-text">

                {(
                  (
                    paidTotal /
                    (
                      paidTotal +
                      outstandingTotal
                    )
                  ) *
                  100
                ).toFixed(1)}
                %

              </strong>

            </div>

          </div>

          <div className="sidebar-card">

            <h3>
              Recent Activity
            </h3>

            <div className="activity-feed">

              {payments
                .slice(0, 5)

                .map((payment) => (

                  <div
                    key={payment.id}
                    className="activity-row"
                  >

                    <div className="activity-dot"></div>

                    <div>

                      <strong>

                        Payment received

                      </strong>

                      <span>

                        $
                        {Number(
                          payment.amount
                        ).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                          }
                        )}

                      </span>

                    </div>

                  </div>

                ))}

            </div>

          </div>

        </div>

      </div>

      {/* ========================================== */}
      {/* MODAL */}
      {/* ========================================== */}

      <NewInvoiceModalModern
        isOpen={showNewModal}
        onClose={() =>
          setShowNewModal(false)
        }
client={selectedClient}
        onSuccess={() =>
          window.location.reload()
        }
      />

    </div>
  );
};

export default Invoices;